import { FC, useMemo, useState } from 'react'
import classNames from 'classnames'
import { ReactComponent as TrendUpIcon } from 'src/assets/svg/trend-up.svg'
import { ReactComponent as ChartIcon } from 'src/assets/svg/ri-bar-chart-box-fill.svg'
import { Skeleton } from 'src/components/Skeleton'
import { PlatformAnalysisLocation } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { TruncatedText } from 'src/pages/ReviewAnalysis/TruncatedText'
import styles from './locationComparison.module.scss'

// Both triangles when the column is inactive; only the active direction's
// triangle when it drives the sort, so a click always visibly changes state
// (the default order already matches some columns — e.g. ranking is rating-
// sorted — where the rows themselves wouldn't move).
const SortIcon: FC<{ dir?: 'asc' | 'desc' }> = ({ dir }) => (
  <svg
    className={styles.sortIcon}
    fill="none"
    height="14"
    viewBox="0 0 14 14"
    width="14"
    xmlns="http://www.w3.org/2000/svg"
  >
    {dir !== 'desc' && (
      <path
        d={
          dir === 'asc'
            ? 'M7 4L10.5 9H3.5L7 4Z'
            : 'M7 2.333L9.333 5H4.667L7 2.333Z'
        }
        fill="currentColor"
      />
    )}
    {dir !== 'asc' && (
      <path
        d={
          dir === 'desc'
            ? 'M7 10L3.5 5H10.5L7 10Z'
            : 'M7 11.667L4.667 9H9.333L7 11.667Z'
        }
        fill="currentColor"
      />
    )}
  </svg>
)

type SortKey =
  | 'oneStarReviews'
  | 'positiveSentiment'
  | 'rank'
  | 'rating'
  | 'reviews'

interface SortState {
  dir: 'asc' | 'desc'
  key: SortKey
}

// Sortable value of a cell: numbers pass through; formatted strings ("4,7",
// "93%") are parsed, comma decimal included.
const numericValue = (value: number | string): number => {
  if (typeof value === 'number') return value
  const parsed = parseFloat(value.replace(',', '.').replace('%', ''))
  return Number.isNaN(parsed) ? 0 : parsed
}

// Top Issue is text, so it does not sort.
const COLUMNS: { key: SortKey | 'topIssue'; label: string }[] = [
  { key: 'rank', label: 'Location' },
  { key: 'rating', label: 'Rating' },
  { key: 'positiveSentiment', label: 'Positive Sentiment' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'oneStarReviews', label: '1★ % Reviews' },
  { key: 'topIssue', label: 'Top Issue' },
]

// Five placeholder rows stand in for the table body, which is otherwise empty
// (and the container collapses to just its header) until the locations land.
const ROW_SKELETON_COUNT = 5
const SKELETON_TEXT_HEIGHT = 18
const SKELETON_WIDTHS = [140, 48, 70, 40, 40, 90]

interface LocationComparisonProps {
  isLoading?: boolean
  locations?: PlatformAnalysisLocation[]
}

const SkeletonRow: FC = () => (
  <div className={styles.tableRow}>
    {SKELETON_WIDTHS.map((width, idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className={styles.cell} key={idx}>
        <Skeleton height={SKELETON_TEXT_HEIGHT} width={width} />
      </div>
    ))}
  </div>
)

const OutlineStar: FC = () => (
  <svg
    className={styles.ratingStar}
    fill="none"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
)

// "↗ 4%" / "↘ 9%"; nothing when there is no baseline.
const SentimentChange: FC<{ change?: number | null }> = ({ change }) =>
  change == null ? null : (
    <span
      className={classNames(styles.change, change < 0 && styles.changeDown)}
    >
      <TrendUpIcon
        className={styles.changeIcon}
        style={change < 0 ? { transform: 'scaleY(-1)' } : undefined}
      />
      {Math.abs(change)}%
    </span>
  )

export const LocationComparison: FC<LocationComparisonProps> = ({
  isLoading = false,
  locations = [],
}) => {
  const [sort, setSort] = useState<SortState>({ dir: 'asc', key: 'rank' })

  // First load only: `locations` is [] both while the query is in flight and
  // when there genuinely are none, so without the loading flag the table just
  // collapsed to its header and then jumped open when the rows arrived.
  const isInitialLoading = isLoading && locations.length === 0

  const handleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { dir: prev.dir === 'asc' ? 'desc' : 'asc', key }
        : // Fresh column: ranking reads top-down, metrics read biggest-first.
          { dir: key === 'rank' ? 'asc' : 'desc', key }
    )

  const sorted = useMemo(() => {
    const rows = [...locations]
    rows.sort((a, b) => {
      const diff = numericValue(a[sort.key]) - numericValue(b[sort.key])
      return sort.dir === 'asc' ? diff : -diff
    })
    return rows
  }, [locations, sort])

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <h3 className={styles.header}>Location Comparison</h3>
          <p className={styles.subtitle}>
            Compare review performance, sentiment and issues across locations
          </p>
        </div>
        {locations.length > 0 && (
          <span className={styles.countChip}>
            <ChartIcon className={styles.countChipIcon} />
            {locations.length}{' '}
            {locations.length === 1 ? 'location' : 'locations'}
          </span>
        )}
      </div>

      <div className={styles.table}>
        <div className={styles.tableHeader}>
          {COLUMNS.map(({ key, label }) =>
            key === 'topIssue' ? (
              <span className={styles.tableHeaderCell} key={key}>
                {label}
              </span>
            ) : (
              <button
                className={classNames(
                  styles.tableHeaderCell,
                  styles.sortable,
                  sort.key === key && styles.tableHeaderCellActive
                )}
                key={key}
                onClick={() => handleSort(key)}
                type="button"
              >
                {label}{' '}
                <SortIcon dir={sort.key === key ? sort.dir : undefined} />
              </button>
            )
          )}
        </div>

        <div className={styles.tableBody}>
          {isInitialLoading &&
            Array.from({ length: ROW_SKELETON_COUNT }).map((_, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <SkeletonRow key={idx} />
            ))}
          {sorted.map((loc) => (
            <div className={styles.tableRow} key={loc.rank}>
              <div className={classNames(styles.cell, styles.cellLocation)}>
                <span
                  className={classNames(
                    styles.rankPill,
                    loc.rank === 1 && styles.rankPillFirst
                  )}
                >
                  #{loc.rank}
                </span>
                <span className={styles.locationName}>{loc.name}</span>
              </div>
              <div className={classNames(styles.cell, styles.cellStrong)}>
                <OutlineStar />
                {loc.rating}
              </div>
              <div className={classNames(styles.cell, styles.cellStrong)}>
                {loc.positiveSentiment}
                <SentimentChange change={loc.positiveSentimentChange} />
              </div>
              <div className={classNames(styles.cell, styles.cellStrong)}>
                {loc.reviews}
              </div>
              <div className={classNames(styles.cell, styles.cellStrong)}>
                {loc.oneStarReviews}
              </div>
              <div className={classNames(styles.cell, styles.cellStrong)}>
                <TruncatedText
                  className={styles.topIssue}
                  text={loc.topIssue ?? 'N/A'}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
